import { useState, type FC, type ReactNode } from "react";
import { Pencil } from "lucide-react";
import { Button } from "@ui/Button";
import { Card } from "@ui/Card";
import type { StepTitle, StepKey } from "@/types/Form";
import { useMultiStepForm } from "@/hooks";
import { StepCard } from "@/components/Cards";
import type { StepProps } from "@/components/FormSteps";
import SummaryStepButtons from "./SummaryStepButtons";

interface SummaryStepProps {
  stepTitle: StepTitle;
  stepKey: StepKey;
  formComponent: FC<StepProps>;
  cardTitle: string;
  cardContent: ReactNode;
}

const SummaryStep: FC<SummaryStepProps> = ({
  stepTitle,
  formComponent: FormComponent,
  stepKey,
  cardContent,
  cardTitle,
}) => {
  const { resetStep, validateStepAndRun } = useMultiStepForm();
  const [isEditing, setIsEditing] = useState(false);

  if (isEditing) {
    return (
      <StepCard stepTitle={stepTitle} className="bg-background border-primary">
        <FormComponent
          additionalItems={
            <SummaryStepButtons
              dataTest={stepKey}
              onCancel={() => {
                resetStep(stepKey);
                setIsEditing(false);
              }}
              onSave={() => {
                void validateStepAndRun(stepKey, () => {
                  setIsEditing(false);
                });
              }}
            />
          }
        />
      </StepCard>
    );
  }

  return (
    <Card
      className="bg-background flex flex-row justify-between gap-2 px-2 py-3 lg:px-8 lg:py-6"
      data-test={`${stepTitle}-summary-card`}
    >
      <div className="w-full space-y-4 leading-[140%]">
        <p className="text-base font-bold">{cardTitle}</p>
        <div className="w-full px-8">{cardContent}</div>
      </div>
      <Button
        type="button"
        onClick={() => setIsEditing(!isEditing)}
        className="size-fit p-px transition hover:opacity-80"
        data-test={`${stepTitle}-summary-edit-button`}
        aria-label={`edit ${stepTitle}`}
        variant="ghost"
      >
        <Pencil className="size-5" strokeWidth={1.5} />
      </Button>
    </Card>
  );
};
export default SummaryStep;
